import { browserStorage } from "@/lib/browser-storage";
import type { SignInMethodName } from "@/types/models";

import { maskEmail, maskMobile } from "./sign-in-hint";

/** How this browser last signed in, so the sign-in page can offer one click back in. It outlives sign-out on purpose. */
export type RememberedSignIn = {
  method: SignInMethodName;
  /** The email or mobile for those methods; null for Google and Apple, which need no contact to continue. */
  identifier: string | null;
  savedAt: number;
};

const STORAGE_KEY = "awraq:last-sign-in";
const METHODS: readonly SignInMethodName[] = ["email", "mobile", "google", "apple"];

const isSignInMethod = (value: unknown): value is SignInMethodName => METHODS.includes(value as SignInMethodName);

export const parseRememberedSignIn = (raw: string | null): RememberedSignIn | null => {
  if (!raw) {
    return null;
  }

  try {
    const candidate = JSON.parse(raw) as Record<string, unknown>;
    const { method, identifier, savedAt } = candidate;

    if (!isSignInMethod(method) || typeof savedAt !== "number") {
      return null;
    }

    const needsIdentifier = method === "email" || method === "mobile";

    if (needsIdentifier && (typeof identifier !== "string" || identifier === "")) {
      return null;
    }

    return { method, identifier: needsIdentifier ? (identifier as string) : null, savedAt };
  } catch {
    return null;
  }
};

export const readRememberedSignIn = (): RememberedSignIn | null =>
  parseRememberedSignIn(browserStorage.getItem(STORAGE_KEY));

export const forgetRememberedSignIn = () => browserStorage.removeItem(STORAGE_KEY);

type SignedInUser = { email?: string | null; mobile?: string | null };

/** Stores the sign-in a page reports through `signedInWith`; an email or mobile sign-in needs that contact on the User. */
export const rememberSignIn = (
  method: unknown,
  user: SignedInUser | null | undefined,
  now = Date.now(),
): RememberedSignIn | null => {
  if (!isSignInMethod(method) || !user) {
    return null;
  }

  const identifier = method === "email" ? user.email : method === "mobile" ? user.mobile : null;

  if ((method === "email" || method === "mobile") && !identifier) {
    return null;
  }

  const remembered: RememberedSignIn = { method, identifier: identifier ?? null, savedAt: now };
  browserStorage.setItem(STORAGE_KEY, JSON.stringify(remembered));

  return remembered;
};

/** The remembered email or mobile as the sign-in page shows it: masked on screen, though the browser stores it in full. */
export const rememberedSignInLabel = ({ method, identifier }: RememberedSignIn): string =>
  method === "mobile" ? maskMobile(identifier ?? "") : maskEmail(identifier ?? "");
